import {
  DocumentReference,
  DocumentSnapshot,
  onSnapshot,
  setDoc,
  SetOptions,
} from "firebase/firestore";
import { Atom, atom } from "jotai";
import { SnapshotResult } from "./types";

export type DocumentResultAtom<T> = Atom<SnapshotResult<T, DocumentSnapshot>>;

const cache = new Map<string, DocumentResultAtom<unknown>>();

export function observeFirestoreDocumentAtom<T>(
  docRef: DocumentReference,
): DocumentResultAtom<T> {
  if (cache.has(docRef.path)) {
    return cache.get(docRef.path)! as DocumentResultAtom<T>;
  }

  const dataAtom = atom<SnapshotResult<T, DocumentSnapshot>>({
    data: undefined,
    loading: true,
    error: undefined,
    snapshot: undefined,
  });

  dataAtom.onMount = (dispatch) => {
    return onSnapshot(
      docRef,
      (snapshot) => {
        dispatch({
          data: { ...snapshot.data(), id: snapshot.id } as T,
          loading: false,
          error: undefined,
          snapshot: snapshot,
        });
      },
      (error) => {
        dispatch({
          data: undefined,
          loading: false,
          error: error,
          snapshot: undefined,
        });
      },
    );
  };

  const resultAtom = atom(
    (get) => {
      return get(dataAtom);
    },
    (_get, _set, data: Partial<T>, options?: SetOptions) => {
      setDoc(docRef, data as Partial<unknown>, { ...options });
    },
  );

  cache.set(docRef.path, resultAtom);

  return resultAtom;
}
